import { all, call, put, takeLatest } from 'redux-saga/effects';
import { PatientsAPI } from '../../../api/patientsAPI';
import { TreatmentPlanTemplatesAPI } from '../../../api/treatmentPlansAPI';
import { PatientDataUniqueType, ToastType } from '../../../utils/interfaces';
import {
  EDIT_PATIENT_ERROR,
  EDIT_PATIENT_REQUEST,
  EDIT_PATIENT_SUCCESS,
  SET_TOAST,
} from '../../actions';
import {ErrorMessageType} from '../../../types/errorMessages';

function* workerEditPatient(action: {
  type: string;
  payload: { patientData: any; applyPlanData: any; navigate: Function; periods: any; id: any };
}): Generator<any> {
  try {
    const { patientData, applyPlanData, navigate, periods, id } = action.payload;
    const requests = [
      call(PatientsAPI.editPatient, patientData, id),
      call(TreatmentPlanTemplatesAPI.applyTreatmentTemplate, applyPlanData),
    ];
    if (periods) requests.push(call(PatientsAPI.saveTemplate, { data: { periods, id } }));

    const result = yield all(requests);
    yield put({
      type: EDIT_PATIENT_SUCCESS,
    });
    if (result && Array.isArray(result) && result[0].errors) {
      const errors = result[0].errors;
      const errorsText = typeof errors === 'string' ? errors : JSON.stringify(errors);
      const isMfaDeliveryConfigError = errorsText?.includes(
        'User does not have delivery config set to turn on SMS_MFA',
      );

      if (isMfaDeliveryConfigError) {
        navigate(`/patients/view/${patientData.id}`);
        yield put({
          type: SET_TOAST,
          payload: {
            open: true,
            message: 'Patient details were updated, but SMS MFA could not be enabled because no SMS delivery configuration is set.',
            type: ToastType.WARNING,
          },
        });
      } else {
        yield put({
          type: SET_TOAST,
          payload: {
            open: true,
            message: 'Something went wrong, please copy details and contact IT support',
            type: ToastType.ERROR,
            isCopy: true,
            copyText: errors,
          },
        });
      }
    } else {
      navigate(`/patients/view/${patientData.id}`);
      yield put({
        type: SET_TOAST,
        payload: {
          open: true,
          message: 'Patient details were successfully updated',
          type: ToastType.SUCCESS,
        },
      });
    }
  } catch (error: any) {
    const errorMessage: ErrorMessageType = {
      text: '',
      type: ''
    };

    switch(true) {
      case error?.response?.data?.includes("uc_sensitivedata_phone"):
        errorMessage.text = "A user with this phone number already exists. Please use another phone number";
        errorMessage.type = PatientDataUniqueType.Phone;
        break;
      case error?.response?.data?.includes("uc_patient_externalid"):
        errorMessage.text = "A user with this ID already exists. Please use another external id";
        errorMessage.type = PatientDataUniqueType.ExternalID;
        break;
      case error?.response?.data?.includes("uc_appuser_name"):
        errorMessage.text = "A user with this login already exists. Please use another login";
        errorMessage.type = PatientDataUniqueType.Username;
        break;
      case error?.response?.data?.includes("uc_sensitivedata_email"):
        errorMessage.text = "A user with this email already exists. Please use another email";
        errorMessage.type = PatientDataUniqueType.Email;
        break;
      default:
        errorMessage.text = "Something went wrong, please check entered data and try again";
    }

    yield put({
      type: SET_TOAST,
      payload: {
        open: true,
        message: errorMessage.text,
        type: ToastType.ERROR,
      }
    });
    
    yield put({
      type: EDIT_PATIENT_ERROR,
      payload: { code: error.response.status, message: errorMessage },
    });
  }
}

export function* watcherEditPatient() {
  yield takeLatest(EDIT_PATIENT_REQUEST, workerEditPatient);
}
